import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useGetJournalSettingsQuery,
  useSetJournalSettingsMutation,
} from '@app/api/Settings';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { CalendarSettings } from '@pages/CalendarSettings/types';

import type { BaseOptionType } from 'antd/es/select';

// Настройки журнала записей филиала: формат времени и показ окна тегов при
// создании записи (us/11144). Оба значения приходят и сохраняются одной
// моделью — GET и POST /Settings/GetJournalSettings; прежние методы
// GetBranchJournalTimeFormat / SetBranchJournalTimeFormat с бэка удалены.
export const useCalendarSettings = () => {
  const { activeBranchId, userId, database } = useUserPrint();
  const { showNotification } = useNotification();
  const {
    userAccessRights: { IsAdmin, IsSuperAdmin },
  } = useNewUserAccessRights();

  const [settings, setSettings] = useState<CalendarSettings>({
    timeFormat: null,
    isRecordTagsWindow: false,
  });

  const [saveJournalSettings, { isLoading: isSavingSettings }] =
    useSetJournalSettingsMutation();

  const { data, isLoading, isFetching, isUninitialized, refetch } =
    useGetJournalSettingsQuery(
      {
        ActiveBranchId: activeBranchId,
        logs: { isLogged: true, userId, database },
      },
      { skip: !activeBranchId }
    );

  const onChange = useCallback(
    (
      key: keyof CalendarSettings,
      value: CalendarSettings[keyof CalendarSettings]
    ) => {
      setSettings((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const isLoadingPage = useMemo(
    () => isLoading || isFetching || isUninitialized,
    [isLoading, isFetching, isUninitialized]
  );

  const options: Array<BaseOptionType> = useMemo(() => {
    if (!data?.TimeFormats) return [];

    return data.TimeFormats.map(({ Id, Value, Key, IsSelected }, index) => ({
      label: Value,
      value: Key,
      key: `${Id ?? index}-${index}`,
      isSelected: IsSelected,
    }));
  }, [data?.TimeFormats]);

  // Переключатель окна тегов по ТЗ доступен владельцу организации. Признака
  // «владелец» в правах нет, ближайшее — администратор организации
  // (гипотеза: IsAdmin / IsSuperAdmin; проверить у Михаила).
  const canEditRecordTagsWindow = Boolean(IsAdmin || IsSuperAdmin);

  const saveSettingsHandler = useCallback(async () => {
    const { timeFormat, isRecordTagsWindow } = settings;

    if (!data || !timeFormat) return;

    try {
      await saveJournalSettings({
        ActiveBranchId: activeBranchId,
        data: {
          ...data,
          TimeFormat: timeFormat,
          IsRecordTagsWindow: isRecordTagsWindow,
        },
      }).unwrap();
      refetch();

      showNotification('Настройки журнала записей сохранены', 'success');
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    data,
    refetch,
    saveJournalSettings,
    settings,
    showNotification,
  ]);

  useEffect(() => {
    if (isLoadingPage || !data) return;

    setSettings((prev) => ({
      timeFormat: data.TimeFormat ?? prev.timeFormat,
      isRecordTagsWindow: data.IsRecordTagsWindow,
    }));
  }, [data, isLoadingPage]);

  return {
    settings,
    onChange,
    isLoadingPage,
    options,
    canEditRecordTagsWindow,
    saveSettingsHandler,
    isSavingSettings,
  };
};
